import { useEffect, useMemo, useState } from "react";

const DEFAULT_RESULT = {};

/**
 * Helps when animating anything that has a transition from a base state to a
 * display state and back. It provides a collection of properties to indicate
 * the current state of the animation: show, active or not. The client is
 * responsible for triggering the start of the "in" animation and the start of
 * the "out" animation.
 */
export function useAnimation(
  elem?: HTMLElement | null
): UseAnimationResult | typeof DEFAULT_RESULT {
  const [animateIn, setAnimateIn] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [show, setShow] = useState(false);
  const startAnimateIn = useMemo(
    () => () => {
      setAnimateIn(true);
      setAnimateOut(false);
      setShow(true);
    },
    []
  );
  const startAnimateOut = useMemo(
    () => () => {
      setAnimateIn(false);
      setAnimateOut(true);
    },
    []
  );
  const result = useMemo(
    () =>
      elem
        ? { animateIn, animateOut, show, startAnimateIn, startAnimateOut }
        : DEFAULT_RESULT,
    [animateIn, animateOut, elem, show, startAnimateIn, startAnimateOut]
  );

  useEffect(() => {
    if (!elem) {
      return;
    }

    function handleAnimationEnd() {
      if (animateOut) {
        setShow(false);
      }

      setAnimateIn(false);
      setAnimateOut(false);
    }

    elem.addEventListener("animationend", handleAnimationEnd);

    return () => {
      elem.removeEventListener("animationend", handleAnimationEnd);
    };
  }, [animateOut, elem, result]);

  return result;
}

export interface UseAnimationResult {
  animateIn: boolean;
  animateOut: boolean;
  /** Show is true as long as the element provided is animating and after the
   * `animateIn` animation has completed. */
  show: boolean;
  startAnimateIn: () => void;
  startAnimateOut: () => void;
}
